import { useState, useEffect, useRef, useMemo } from 'react';

const inMemoryStore = new Map();

const createStateHandle = (key, initialValue) => {
  const callbacks = new Set();

  let value = initialValue;
  const setValue = (newValue) => {
    if (typeof newValue === 'function') {
      value = newValue(value);
    } else {
      value = newValue;
    }
    callbacks.forEach((callback) => callback(value));
  };
  const listen = (callback) => {
    callbacks.add(callback);
    callback(value);
  };
  const stopListening = (callback) => {
    callbacks.delete(callback);
    if (callbacks.size === 0) {
      inMemoryStore.delete(key);
    }
  };
  const getValue = () => value;

  const existingState = {
    getValue,
    setValue,
    listen,
    stopListening
  };

  inMemoryStore.set(key, existingState);

  return existingState;
};

const getOrCreateStateHandle = (key, initialValue) => {
  return inMemoryStore.get(key) || createStateHandle(key, initialValue);
};

export const useSharedState = (key, initialValue = undefined) => {
  const stateHandle = useMemo(
    () => getOrCreateStateHandle(key, initialValue),
    [key]
  );
  const [value, setValue] = useState(stateHandle.getValue());
  const previousSetValue = useRef();
  const previousStateHandle = useRef();

  useEffect(() => {
    if (previousStateHandle.current) {
      previousStateHandle.current.stopListening(previousSetValue.current);
    }

    previousStateHandle.current = stateHandle;
    previousSetValue.current = setValue;

    stateHandle.listen(setValue);

    return () => {
      previousStateHandle.current.stopListening(previousSetValue.current);
    };
  }, [key]);

  return useMemo(
    () => [value, stateHandle.setValue],
    [value, stateHandle.setValue]
  );
};

export const getState = (key) => {
  return inMemoryStore.get(key)?.getValue();
};

export const setState = (key, value) => {
  const handle = inMemoryStore.get(key);

  if (handle) {
    handle.setValue(value);
  }
};
